/**
 * `Retry-After` for the load-error state (TMMAPP-0068, TMM-B3). The vendored factory's `ApiError`
 * never carries response headers, so `client.ts` adds `retryAfterTransform` to the instance's
 * response transforms: it runs on the raw error response (before the factory normalizes it) and
 * copies a 429 / 503 `Retry-After` into the envelope's `error.details` bag as `retryAfterSeconds`,
 * which the factory already passes through as `ApiError.details.backend`. `retryAfterSeconds`
 * reads it back; nothing else in the app looks at the header.
 */
import { isApiError } from './errors';

/** The key the transform writes into `error.details`. */
export const RETRY_AFTER_KEY = 'retryAfterSeconds';

/** The longest delay the SPA holds Retry back for: a larger (or absurd) value is clamped to this. */
export const MAX_RETRY_AFTER_SECONDS = 300;

/** A `429` or `503` is the only answer whose `Retry-After` the SPA honours. */
const HONOURED_STATUSES = new Set([429, 503]);

/**
 * `Retry-After` is delta-seconds or an HTTP date (RFC 9110 §10.2.3). Returns whole seconds
 * (rounded up, at least 1, at most `MAX_RETRY_AFTER_SECONDS`) or `null` for anything else — a past
 * date, a negative or fractional delta, text.
 */
export function parseRetryAfter(value: unknown, nowMs: number = Date.now()): number | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (/^\d+$/.test(text)) {
    const seconds = Number(text);
    return seconds > 0 ? Math.min(seconds, MAX_RETRY_AFTER_SECONDS) : null;
  }
  if (text.length === 0 || /^[-+.\d]+$/.test(text)) return null;
  const at = Date.parse(text);
  if (Number.isNaN(at)) return null;
  const seconds = Math.ceil((at - nowMs) / 1000);
  return seconds > 0 ? Math.min(seconds, MAX_RETRY_AFTER_SECONDS) : null;
}

interface HeaderReader {
  get?: (name: string) => unknown;
}

/**
 * An axios `transformResponse` step (placed after the default JSON parse): on a 429 / 503 with a
 * usable `Retry-After`, the delay joins the body's `error.details`. A body that is not an
 * envelope (an edge's HTML page) becomes an envelope that carries only the delay — the factory
 * reads `raw` only to say "could not classify", and nothing renders it.
 */
export function retryAfterTransform(data: unknown, headers: unknown, status?: number): unknown {
  if (status === undefined || !HONOURED_STATUSES.has(status)) return data;
  const reader = headers as HeaderReader | null | undefined;
  const seconds = parseRetryAfter(
    typeof reader?.get === 'function' ? reader.get('retry-after') : undefined,
  );
  if (seconds === null) return data;
  const body = typeof data === 'object' && data !== null && !Array.isArray(data) ? data : {};
  const envelope = body as { error?: unknown };
  const error = typeof envelope.error === 'object' && envelope.error !== null ? envelope.error : {};
  const details = (error as { details?: unknown }).details;
  return {
    ...body,
    error: {
      ...error,
      details: {
        ...(typeof details === 'object' && details !== null ? details : {}),
        [RETRY_AFTER_KEY]: seconds,
      },
    },
  };
}

/** The seconds a person should wait before retrying `error`, or `null` when none was given. */
export function retryAfterSeconds(error: unknown): number | null {
  if (!isApiError(error) || !HONOURED_STATUSES.has(error.status)) return null;
  const value = error.details.backend?.[RETRY_AFTER_KEY];
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}
